var Service                   = require("app/Domain/Ott/Api/V2/Service"),
    circularSafeJSONStringify = require("app/utils/circularSafeJSONStringify"),
    config                    = require("app/config"),
    kind                      = require("enyo/kind"),
    logger                    = require("enyo/logger"),
    quickMacroize             = require("enyo/macroize").quickMacroize;

/**
 * This service manages a session for the current user.
 */
module.exports = kind.singleton(/** @lends app.Domain.Ott.Api.V2.DataAccess.UserSession.Service.prototype */ {

    /**
     * @private
     */
    name: "app.Domain.Ott.Api.V2.DataAccess.UserSession.Service",

    /**
     * @private
     */
    kind: Service,

    // API
    // --------------------------------

    /**
     * Login with email and password
     *
     * @param    {string}   email     The email
     * @param    {string}   password  The password
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    login: function(email, password) {
        var key  = ["user", "login"].join("/"),
            task = this.tasks[key],
            endpoint;

        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/user/login", {
                ottApi: config.ottApi.url
            }));

            task = this.tasks[endpoint] = this._buildXhrTask();

            task.execute = function() {
                var xhr;

                task.xhr = xhr = new XMLHttpRequest();
                xhr.open("POST", endpoint);
                xhr.setRequestHeader("Content-Type", "text/plain");

                xhr.onreadystatechange = function() {
                    if (xhr.readyState === 4) {
                        if (xhr.status === 0 && task.isRenewing) {
                        // We are renewing the task, we can skip handling the
                        // canceled response and await the result of the renewed
                        // task.
                        }
                        else {
                            task.handleResult();
                        }
                    }
                };
                xhr.onreadystatechange.bind(this);
                xhr.send(JSON.stringify({
                    user:     email,
                    password: password
                }));
            };

            task.handleResult = function() {
                var response,
                    xhr     = task.xhr,
                    resolve = task.resolve;

                try {
                    response = xhr.responseText || null;
                }
                catch (error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error, {
                            extra: {
                                xhr: circularSafeJSONStringify(xhr)
                            }
                        });
                    }
                    response = null;
                }

                switch (xhr.status) {
                    case 0:
                        resolve({
                            status: "canceled"
                        });
                        break;
                    case 200:
                        resolve({
                            status: "ok"
                        });
                        break;
                    case 404:
                        resolve({
                            status: "error",
                            type:   "invalid_credentials"
                        });
                        break;
                    default:
                        resolve({
                            status:     "error",
                            errorType:  response && response.type ? response.type : "unknown_error",
                            httpStatus: xhr.status
                        });
                        break;
                }
            };

            task.promise
                .then(this.bindSafely(function(response) {
                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        // Run the task
        task.execute();

        return task.promise;
    },

    /**
     *
     * @param    {String}   sessionKey  The session key
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    authenticate: function(sessionKey) {
        var key  = ["user", "auth"].join("/"),
            task = this.tasks[key],
            endpoint;

        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/user/auth", {
                ottApi: config.ottApi.url
            }));

            task = this.tasks[endpoint] = this._buildXhrTask();

            task.execute = function() {
                var xhr;

                task.xhr = xhr = new XMLHttpRequest();
                xhr.open("POST", endpoint);
                xhr.setRequestHeader("Content-Type", "text/plain");

                xhr.onreadystatechange = function() {
                    if (xhr.readyState === 4) {
                        if (xhr.status === 0 && task.isRenewing) {
                        // We are renewing the task, we can skip handling the
                        // canceled response and await the result of the renewed
                        // task.
                        }
                        else {
                            task.handleResult();
                        }
                    }
                };
                xhr.onreadystatechange.bind(this);
                xhr.send(JSON.stringify({
                    session_key: sessionKey
                }));
            };

            task.handleResult = function() {
                var response,
                    xhr     = task.xhr,
                    resolve = task.resolve;

                try {
                    response = xhr.responseText || null;
                }
                catch (error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error, {
                            extra: {
                                xhr: circularSafeJSONStringify(xhr)
                            }
                        });
                    }
                    response = null;
                }

                switch (xhr.status) {
                    case 0:
                        resolve({
                            status: "canceled"
                        });
                        break;
                    case 200:
                        resolve({
                            status: "ok"
                        });
                        break;
                    default:
                        resolve({
                            status:     "error",
                            errorType:  response && response.type ? response.type : "unknown_error",
                            httpStatus: xhr.status
                        });
                        break;
                }
            };

            task.promise
                .then(this.bindSafely(function(response) {
                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        // Run the task
        task.execute();

        return task.promise;
    },

    /**
     * Logs the user out of the current session.
     *
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    logout: function() {
        var key  = ["user", "logout"].join("/"),
            task = this.tasks[key],
            endpoint;

        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/user/logout", {
                ottApi: config.ottApi.url
            }));

            task = this.tasks[endpoint] = this._buildXhrTask();

            task.execute = function() {
                var xhr;

                task.xhr = xhr = new XMLHttpRequest();
                xhr.open("GET", endpoint);
                xhr.setRequestHeader("Content-Type", "text/plain");

                xhr.onreadystatechange = function() {
                    if (xhr.readyState === 4) {
                        if (xhr.status === 0 && task.isRenewing) {
                        // We are renewing the task, we can skip handling the
                        // canceled response and await the result of the renewed
                        // task.
                        }
                        else {
                            task.handleResult();
                        }
                    }
                };
                xhr.onreadystatechange.bind(this);
                xhr.send();
            };

            task.handleResult = function() {
                var response,
                    xhr     = task.xhr,
                    resolve = task.resolve;

                try {
                    response = xhr.responseText || null;
                }
                catch (error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error, {
                            extra: {
                                xhr: circularSafeJSONStringify(xhr)
                            }
                        });
                    }
                    response = null;
                }

                switch (xhr.status) {
                    case 0:
                        resolve({
                            status: "canceled"
                        });
                        break;
                    case 200:
                        resolve({
                            status: "ok"
                        });
                        break;
                    default:
                        resolve({
                            status:     "error",
                            errorType:  response && response.type ? response.type : "unknown_error",
                            httpStatus: xhr.status
                        });
                        break;
                }
            };

            task.promise
                .then(this.bindSafely(function(response) {
                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        // Run the task
        task.execute();

        return task.promise;
    },

    /**
     * Request a password reset e-mail for a particular user.
     *
     * @param    {string}   email  The email
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    requestPasswordReset: function(email) {
        var key  = ["password-reset", "request"].join("/"),
            task = this.tasks[key],
            endpoint;

        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/password-reset/request", {
                ottApi: config.ottApi.url
            }));

            task = this.tasks[key] = this._post(endpoint.toString(), {
                user: email
            });

            task.promise
                .then(this.bindSafely(function(response) {
                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        return task.promise;
    }

});
